import moment from "moment";
import { shortLabel } from "./policyParts";
import { friendlyError } from "./friendlyError";

/**
 * The History page's Activity in plain words (2026-10-22: "Completed → Waiting for the automation · KSHEMA steps set by
 * hand (KYC is next) — re-queued OD + TP" confused everyone). One entry of the policy's statusHistory becomes:
 *
 *   { kind, who, text, detail, tone, badge }
 *     who    the person, when it was one ("Anmol") - shown in bold
 *     text   what happened, as a sentence that reads after the name ("edited the policy - OD + TP will run again")
 *     detail one more line when there is something to add (the reason given, the insurer's words)
 *     tone   success | error | warning | info | navy | default (the icon's colour)
 *     badge  a short tag for the one thing worth calling out ("Re-submitted after completion")
 *
 * New entries carry `kind` (and `by` / `byName`) from the backend; older ones are recognised from their note, so every
 * policy reads the same way. The raw entry stays available ("Show details") for whoever needs it.
 */

const STAGE = { proposal: "Proposal", kyc: "KYC", payment: "Payment" };

/** "od,tp" / ["od","tp"] / "od" -> "OD and TP". */
export const partsWords = (entry = {}) => {
  const raw = entry.parts || entry.part;
  const keys = (Array.isArray(raw) ? raw : String(raw || "").split(",")).map((k) => String(k).trim()).filter(Boolean);
  return keys.map(shortLabel).join(" and ");
};

/** Which kind of entry this is - the backend's own `kind`, or worked out from an older entry's note. */
export function kindOf(entry = {}) {
  if (entry.kind) return entry.kind;
  const note = String(entry.note || entry.reason || "");
  const to = String(entry.to || "").toLowerCase();
  const from = String(entry.from || "").toLowerCase();
  const queued = /^job (created|updated|re-created) (on|after) /i.test(note);
  if (queued) {
    if (/until the wallet has money/i.test(note)) return "saved-held";
    if (/re-submitted after completion/i.test(note)) return "resubmitted";
    if (/released from hold/i.test(note)) return "released";
    if (/policy (edited|updated)/i.test(note)) return "edited";
    if (/policy retry\b/i.test(note)) return "retried";
    if (/New KYC files/i.test(note)) return "kyc-files-run";
    if (/TP policy details entered/i.test(note)) return "tp-details-run";
    if (/steps set by hand|set back to pending/i.test(note)) return "stage-run";
    if (!from || /on policy created/i.test(note)) return "created";
    return "queued";
  }
  if (/held by operator/i.test(note)) return "held";
  if (/^All steps completed by hand/i.test(note)) return "stages-done-by-hand";
  if (/ steps set by hand/i.test(note)) return "stages-by-hand";
  if (/set back to pending by hand/i.test(note)) return "stage-reset";
  if (/^KYC files changed by hand/i.test(note)) return "kyc-files";
  if (/the policy was edited, so a new proposal/i.test(note)) return "stages-cleared";
  if (/Brisk certificate uploaded manually/i.test(note)) return "brisk-manual";
  if (/was set back to pending with Only update - run now/i.test(note)) return "stage-run";
  if (to === "processing") return "started";
  if (!from) return "created";
  if (entry.part) return "part-result";
  return "other";
}

const insurerOf = (entry) => {
  if (entry.insurer) return entry.insurer;
  const m = String(entry.note || "").match(/^(Reliance|KSHEMA|National)\b/i) || String(entry.note || "").match(/policy (Reliance|KSHEMA|National) /i);
  return m ? m[1] : "";
};
const reasonOf = (entry) => {
  if (entry.reason && entry.kind) return entry.reason;
  const m = String(entry.note || "").match(/ steps set by hand - (.+?)( \(referral number [^)]+\))?$/i);
  return m ? m[1].trim() : "";
};
const nextStageOf = (entry) => {
  const m = String(entry.note || entry.reason || "").match(/\((Proposal|KYC|Payment) is next\)/i);
  return m ? m[1] : "";
};
const stageOf = (entry) => {
  if (entry.stage && STAGE[entry.stage]) return STAGE[entry.stage];
  const m = String(entry.note || "").match(/(Proposal|KYC|Payment) (was )?set back to pending/i);
  return m ? m[1] : "";
};
const willRun = (entry) => {
  const parts = partsWords({ parts: entry.parts }) || (String(entry.note || "").match(/re-queued (.+)$/i) || [])[1] || "";
  return parts ? ` - ${parts.replace(/, /g, " and ")} will run again` : "";
};

/** A part's result from the automation. */
function partResult(entry, part) {
  const to = String(entry.to || "").toLowerCase();
  const on = part ? ` ${part}` : "";
  if (to === "completed") return { text: `The automation finished${on} - completed`, tone: "success" };
  if (to === "completed_with_errors") return { text: `The automation finished${on} - with a warning`, tone: "warning", detail: entry.note && !/^Completed/i.test(entry.note) ? entry.note : "" };
  if (to === "hold" || /held by the operator/i.test(entry.note || "")) return { text: `The automation stopped${on} - held by the operator`, tone: "warning" };
  const why = entry.note ? friendlyError(entry.note).title : "";
  if (to === "pending") return { text: `The automation stopped${on} - it will try again`, tone: "warning", detail: why };
  if (to === "failed_post_submission") return { text: `The automation stopped${on} after the proposal was saved - not tried again automatically`, tone: "error", detail: why };
  if (to.startsWith("failed")) return { text: `The automation failed${on}`, tone: "error", detail: why };
  return { text: `The automation updated${on}`, tone: "default", detail: why };
}

/** One statusHistory entry in plain words. */
export function activityLine(entry = {}) {
  const kind = kindOf(entry);
  const who = entry.byName || null;
  const part = partsWords(entry);
  const after = (sentence, passive) => (who ? { who, text: sentence } : { who: null, text: passive });
  const still = /still on hold/i.test(entry.note || "") ? " (it stays on hold)" : "";

  switch (kind) {
    case "created":
      return { kind, ...after("created the policy", "The policy was created"), tone: "navy", detail: "" };
    case "saved-held":
      return { kind, ...after("saved the policy on hold", "The policy was saved on hold"), tone: "warning", detail: "It waits until the wallet has money - nothing was charged" };
    case "edited":
      return { kind, ...after(`edited the policy${willRun(entry)}${still}`, `The policy was edited${willRun(entry)}${still}`), tone: "navy", detail: "" };
    case "retried":
      return { kind, ...after(`pressed Retry${willRun(entry)}`, `Retry was pressed${willRun(entry)}`), tone: "info", detail: "" };
    case "resubmitted":
      return {
        kind,
        ...after(`sent the policy again after it was completed${willRun(entry)}`, `The policy was sent again after it was completed${willRun(entry)}`),
        tone: "warning",
        detail: "Allowed because it is paid by payment link and was not paid yet",
        badge: "Re-submitted after completion",
      };
    case "released":
      return { kind, ...after("removed the hold - the automation can start it", "The hold was removed - the automation can start it"), tone: "success", detail: "" };
    case "held":
      return {
        kind,
        ...after("put the policy on hold", "The policy was put on hold"),
        tone: "warning",
        detail: /asked while it was running/i.test(entry.note || "") ? "Asked while the automation was running - applied when that run ended" : "The automation will not start it until it is released",
      };
    case "stage-run": {
      const next = nextStageOf(entry);
      return { kind, ...after(`sent it back to the automation${next ? ` - ${next} is next` : ""}`, `Sent back to the automation${next ? ` - ${next} is next` : ""}`), tone: "info", detail: "" };
    }
    case "tp-details":
      return { kind, ...after("entered the TP policy details", "The TP policy details were entered"), tone: "navy", detail: String(entry.note || "") };
    case "tp-details-run":
      return { kind, ...after("sent Reliance OD back to the automation - the TP policy details are in", "Reliance OD sent back to the automation - the TP policy details are in"), tone: "info", detail: "" };
    case "kyc-files-run":
      return { kind, ...after("sent it back to the automation for the KYC", "Sent back to the automation for the KYC"), tone: "info", detail: "" };
    case "stages-by-hand": {
      const ins = insurerOf(entry);
      const changes = entry.from && entry.to ? `${entry.from} → ${entry.to}` : "";
      const reason = reasonOf(entry);
      return {
        kind,
        ...after(`set the ${ins ? `${ins} ` : ""}steps by hand`, `The ${ins ? `${ins} ` : ""}steps were set by hand`),
        tone: "navy",
        detail: [changes, reason ? `Reason - ${reason}` : ""].filter(Boolean).join(" · "),
      };
    }
    case "stages-done-by-hand":
      return { kind, ...after("marked every step completed by hand", "Every step was marked completed by hand"), tone: "success", detail: reasonOf(entry) || String(entry.note || "").replace(/^All steps completed by hand( - )?/i, "") };
    case "stage-reset": {
      const ins = insurerOf(entry);
      const stage = stageOf(entry);
      return {
        kind,
        ...after(`set ${ins ? `${ins} ` : ""}${stage || "a step"} back to pending`, `${ins ? `${ins} ` : ""}${stage || "A step"} was set back to pending`),
        tone: "warning",
        detail: /a new proposal is made/i.test(entry.note || "") ? "The old proposal's KYC links were cleared - a new proposal is made" : "",
      };
    }
    case "kyc-files":
      return { kind, ...after("changed the KYC files", "The KYC files were changed"), tone: "navy", detail: /only the KYC/i.test(entry.note || "") ? "Only the KYC runs again" : "" };
    case "stages-cleared":
      return { kind, who: null, text: `The ${insurerOf(entry) || "insurer"} steps start again because the policy was edited`, tone: "default", detail: "A new proposal, KYC and payment are made" };
    case "brisk-manual":
      return { kind, ...after("uploaded the Brisk certificate by hand", "The Brisk certificate was uploaded by hand"), tone: "success", detail: "" };
    case "started":
      return { kind, who: null, text: `The automation started${part ? ` ${part}` : ""}`, tone: "info", detail: "" };
    case "part-result":
      return { kind, who: null, ...partResult(entry, part) };
    default: {
      const note = String(entry.note || entry.reason || "").replace(/^job (created|updated|re-created) (on|after) (policy )?/i, "");
      return { kind: "other", ...after(note || "updated the policy", note ? note.charAt(0).toUpperCase() + note.slice(1) : "The policy was updated"), tone: "default", detail: "" };
    }
  }
}

/** "Today" / "Yesterday" / "21 Oct 2026" - the day heading of an Activity group. */
export function dayLabel(date, now = new Date()) {
  const d = moment(date);
  if (!d.isValid()) return "Unknown date";
  const today = moment(now).startOf("day");
  if (d.isSame(today, "day")) return "Today";
  if (d.isSame(moment(today).subtract(1, "day"), "day")) return "Yesterday";
  return d.format("DD MMM YYYY");
}

/** Entries newest first, grouped by day: [{ day, entries }]. */
export function groupByDay(entries = [], now = new Date()) {
  const sorted = [...entries].sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
  const groups = [];
  for (const e of sorted) {
    const day = dayLabel(e.timestamp, now);
    const last = groups[groups.length - 1];
    if (last && last.day === day) last.entries.push(e);
    else groups.push({ day, entries: [e] });
  }
  return groups;
}

/** Whether the "re-submitted after completion" count should include this entry (only a real re-submit). */
export const isResubmit = (entry) => kindOf(entry) === "resubmitted";
